import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { findRouteByPath, pagePath } from "@/content/seo";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, LogOut, ChevronDown, Menu, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

export function Brand({ size = "md", to = "/" }) {
  const h = size === "sm" ? "h-7" : "h-9";
  return (
    <Link to={to} className="flex items-center gap-2.5 group" data-testid="brand-link">
      <img src="/brand/logo-icon.png" alt="EmpCo" className={`${h} w-auto transition-transform duration-300 group-hover:rotate-12`} />
      <span className="font-sans font-bold tracking-tight text-primary text-xl leading-none">EMPCO</span>
    </Link>
  );
}

const linkCls = "text-muted-foreground hover:text-primary transition-colors duration-200";

const NAV = {
  product: { fr: "Produit", en: "Product", de: "Produkt", es: "Producto", it: "Prodotto", nl: "Product" },
  freescan: { fr: "Scan gratuit", en: "Free scan", de: "Gratis-Scan", es: "Escaneo gratuito", it: "Scansione gratuita", nl: "Gratis scan" },
};

export default function Header({ variant = "public" }) {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Bascule de langue : on reste sur la page équivalente (hreflang réciproque)
  const switchLang = (l) => {
    setLang(l);
    const current = findRouteByPath(location.pathname);
    if (current) nav(pagePath(current.key, l));
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-border">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 h-[64px] md:h-[68px] flex items-center justify-between gap-2">
        <Brand />

        <nav className="hidden md:flex items-center gap-6 lg:gap-8 text-sm font-medium">
          {variant === "public" && (
            <>
              <a href="/#method" className={linkCls} data-testid="nav-method">{t("nav.method")}</a>
              <Link to={pagePath("pricing", lang)} className={linkCls} data-testid="nav-pricing">{t("nav.pricing")}</Link>
              <Link to={pagePath("product", lang)} className={linkCls} data-testid="nav-product">{NAV.product[lang] || NAV.product.en}</Link>
              <Link to={pagePath("freescan", lang)} className={linkCls} data-testid="nav-freescan">{NAV.freescan[lang] || NAV.freescan.en}</Link>
              <Link to={pagePath("guides", lang)} className={linkCls} data-testid="nav-guides">{t("nav.guides")}</Link>
              <Link to="/contact" className={linkCls} data-testid="nav-contact">{t("nav.contact")}</Link>
            </>
          )}
          {user && variant === "app" && (
            <Link to="/app" className={linkCls} data-testid="nav-dashboard">{t("nav.dashboard")}</Link>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger className="inline-flex items-center gap-1 px-2 py-1.5 text-xs border border-border hover:border-primary hover:text-primary transition-colors rounded-md font-mono" data-testid="lang-switcher">
              {lang.toUpperCase()} <ChevronDown className="w-3 h-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => switchLang("fr")} data-testid="lang-fr">Français</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">English</DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchLang("de")} data-testid="lang-de">Deutsch</DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchLang("es")} data-testid="lang-es">Español</DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchLang("it")} data-testid="lang-it">Italiano</DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchLang("nl")} data-testid="lang-nl">Nederlands</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {!user ? (
            <>
              <Link to="/login" className="hidden sm:inline-flex text-sm font-medium text-primary hover:text-[hsl(var(--primary-light))] px-2" data-testid="nav-login-btn">
                {t("nav.login")}
              </Link>
              <Link to="/register" className="hidden sm:inline-flex btn-primary !py-2 !px-4" data-testid="nav-register-btn">
                {t("auth.register")}
              </Link>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-2 pl-1.5 pr-2 sm:pr-3 py-1.5 text-sm border border-border hover:border-primary transition-colors rounded-full" data-testid="user-menu-trigger">
                <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-semibold">
                  {(user.first_name || "?")[0]}
                </span>
                <span className="hidden sm:inline font-medium">{user.company || `${user.first_name} ${user.last_name}`}</span>
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => nav("/app")} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> {t("nav.dashboard")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Hamburger mobile */}
          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md border border-border hover:border-primary text-slate-700"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-white" data-testid="mobile-menu">
          <nav className="px-4 py-4 flex flex-col gap-1 text-sm font-medium">
            {variant === "public" && (
              <>
                <a onClick={closeMobile} href="/#method" className="px-3 py-2.5 rounded-md hover:bg-accent" data-testid="mobile-nav-method">{t("nav.method")}</a>
                <Link onClick={closeMobile} to={pagePath("pricing", lang)} className="px-3 py-2.5 rounded-md hover:bg-accent" data-testid="mobile-nav-pricing">{t("nav.pricing")}</Link>
                <Link onClick={closeMobile} to={pagePath("product", lang)} className="px-3 py-2.5 rounded-md hover:bg-accent" data-testid="mobile-nav-product">{NAV.product[lang] || NAV.product.en}</Link>
                <Link onClick={closeMobile} to={pagePath("freescan", lang)} className="px-3 py-2.5 rounded-md hover:bg-accent" data-testid="mobile-nav-freescan">{NAV.freescan[lang] || NAV.freescan.en}</Link>
                <Link onClick={closeMobile} to={pagePath("guides", lang)} className="px-3 py-2.5 rounded-md hover:bg-accent" data-testid="mobile-nav-guides">{t("nav.guides")}</Link>
                <Link onClick={closeMobile} to="/contact" className="px-3 py-2.5 rounded-md hover:bg-accent" data-testid="mobile-nav-contact">{t("nav.contact")}</Link>
                <Link onClick={closeMobile} to="/faq" className="px-3 py-2.5 rounded-md hover:bg-accent" data-testid="mobile-nav-faq">FAQ</Link>
                <Link onClick={closeMobile} to="/legal" className="px-3 py-2.5 rounded-md hover:bg-accent" data-testid="mobile-nav-directive">Directive UE 2024/825</Link>
              </>
            )}
            {!user && (
              <div className="border-t border-border mt-2 pt-3 flex flex-col gap-2">
                <Link onClick={closeMobile} to="/login" className="px-3 py-2.5 rounded-md text-primary font-semibold hover:bg-accent" data-testid="mobile-nav-login">{t("nav.login")}</Link>
                <Link onClick={closeMobile} to="/register" className="btn-primary text-center !py-2.5" data-testid="mobile-nav-register">{t("auth.register")}</Link>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
